import jsPDF from 'jspdf';
import { BRAND_MARK, BRAND_WORDMARK } from './brand';

export const A4_PAGE_WIDTH = 210;
export const A4_PAGE_HEIGHT = 297;
export const A4_MARGIN = 20;

export const TEAL_RGB: [number, number, number] = [0, 162, 154];
export const TEAL_LIGHT_RGB: [number, number, number] = [240, 253, 250];
export const TEAL_BORDER_RGB: [number, number, number] = [204, 251, 241];

export interface CompanyInfo {
  ragioneSociale: string;
  indirizzo: string;
  cap: string;
  citta: string;
  provincia: string;
  partitaIva: string;
  codiceFiscale: string;
  rea: string;
  regimeFiscale: string;
  regimeFiscaleCode: string;
  email: string;
  pec: string;
  telefono: string;
  sito: string;
  iban: string;
  banca: string;
}

export const COMPANY_INFO: CompanyInfo = {
  ragioneSociale: 'ABRUZZO.AI di Appicciutoli Federica',
  indirizzo: 'Via Fosso Foreste, 8/2 Sc. A Int. 9',
  cap: '65015',
  citta: 'Montesilvano',
  provincia: 'PE',
  partitaIva: '02465100689',
  codiceFiscale: 'PPCFRC98P41A488G',
  rea: 'PE-439258',
  regimeFiscale: 'Regime forfettario (L. 190/2014)',
  regimeFiscaleCode: 'RF19',
  email: 'info@abruzzo.ai',
  pec: 'abruzzo.ai@pec.it',
  telefono: '+39 320 356 6659',
  sito: 'www.abruzzo.ai',
  iban: 'IT18A0538777340000004767910',
  banca: 'BPER BANCA',
};

export const COMPANY_FOOTER_DEFAULT =
  `${COMPANY_INFO.ragioneSociale} — ${COMPANY_INFO.indirizzo}, ${COMPANY_INFO.cap} ${COMPANY_INFO.citta} (${COMPANY_INFO.provincia}) — P.IVA ${COMPANY_INFO.partitaIva} — C.F. ${COMPANY_INFO.codiceFiscale} — REA ${COMPANY_INFO.rea} — PEC ${COMPANY_INFO.pec} — ${COMPANY_INFO.email} — Tel. ${COMPANY_INFO.telefono}`;

export const MARCHIO_PUBLIC_PATH = BRAND_MARK;
export const LOGOTIPO_PUBLIC_PATH = BRAND_WORDMARK;

export interface LetterheadCustomer {
  company_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  province?: string | null;
  vat_number?: string | null;
  tax_code?: string | null;
  phone?: string | null;
  [key: string]: unknown;
}

/** Carica un'immagine pubblica come data URL (base64) per jsPDF.addImage. */
export const loadImageAsDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Impossibile caricare: ${url}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Carica un'immagine e la ridimensiona a `maxWidthPx` (lato lungo) prima di
 * restituirla come PNG data URL. Le immagini di origine (es. marchio 688×697px,
 * ~640KB) vengono incorporate da jsPDF a piena risoluzione anche se mostrate a
 * pochi mm: scalarle alla dimensione reale di stampa riduce il peso del PDF da
 * megabyte a pochi KB. Mantiene la trasparenza (PNG).
 */
export const loadImageScaledDataUrl = async (
  url: string,
  maxWidthPx: number
): Promise<string> => {
  const original = await loadImageAsDataUrl(url);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidthPx / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(original); // fallback: meglio un PDF grande che nessun PDF
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = original;
  });
};

/** Disegna marchio + logotipo + linea di separazione teal in cima alla pagina. */
export const drawLetterhead = (
  doc: jsPDF,
  marchioDataUrl: string,
  logotipoDataUrl: string,
  pageWidth: number = A4_PAGE_WIDTH,
  margin: number = A4_MARGIN
): void => {
  const marchioSize = 18;
  doc.addImage(marchioDataUrl, 'PNG', (pageWidth - marchioSize) / 2, 8, marchioSize, marchioSize);

  const logoProps = doc.getImageProperties(logotipoDataUrl);
  const logoW = 48;
  const logoH = (logoProps.height / logoProps.width) * logoW;
  const logoY = 8 + marchioSize + 2;
  doc.addImage(logotipoDataUrl, 'PNG', (pageWidth - logoW) / 2, logoY, logoW, logoH);

  const lineY = logoY + logoH + 2;
  doc.setDrawColor(...TEAL_RGB);
  doc.setLineWidth(0.5);
  doc.line(margin, lineY, pageWidth - margin, lineY);
};

/**
 * Disegna box "Dati Emittente" (sinistra) + "Spett.le Cliente" (destra) affiancati.
 * Ritorna la nuova yPos sotto i due box.
 */
export const drawIssuerAndCustomerBoxes = (
  doc: jsPDF,
  customer: LetterheadCustomer | undefined | null,
  yStart: number = 48,
  pageWidth: number = A4_PAGE_WIDTH,
  margin: number = A4_MARGIN
): number => {
  const gap = 4;
  const boxW = (pageWidth - 2 * margin - gap) / 2;
  const boxH = 52;

  // Dati Emittente (sinistra)
  doc.setFillColor(...TEAL_LIGHT_RGB);
  doc.setDrawColor(...TEAL_BORDER_RGB);
  doc.setLineWidth(0.5);
  doc.roundedRect(margin, yStart, boxW, boxH, 3, 3, 'FD');

  let yL = yStart + 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL_RGB);
  doc.text('DATI EMITTENTE', margin + 3, yL);

  yL += 4;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(COMPANY_INFO.ragioneSociale, margin + 3, yL);

  yL += 4;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);
  const issuerLines = [
    COMPANY_INFO.indirizzo,
    `${COMPANY_INFO.cap} ${COMPANY_INFO.citta} (${COMPANY_INFO.provincia})`,
    `P.IVA: ${COMPANY_INFO.partitaIva}`,
    `C.F.: ${COMPANY_INFO.codiceFiscale}`,
    `REA: ${COMPANY_INFO.rea}`,
    `Regime fiscale: ${COMPANY_INFO.regimeFiscaleCode} - ${COMPANY_INFO.regimeFiscale}`,
    `PEC: ${COMPANY_INFO.pec}`,
    `Email: ${COMPANY_INFO.email}  Tel: ${COMPANY_INFO.telefono}`,
  ];
  issuerLines.forEach((line) => {
    doc.text(line, margin + 3, yL);
    yL += 3.6;
  });

  // Spett.le Cliente (destra)
  const xR = margin + boxW + gap;
  doc.setFillColor(...TEAL_LIGHT_RGB);
  doc.setDrawColor(...TEAL_BORDER_RGB);
  doc.roundedRect(xR, yStart, boxW, boxH, 3, 3, 'FD');

  let yR = yStart + 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL_RGB);
  doc.text('SPETT.LE CLIENTE', xR + 3, yR);

  yR += 4;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(customer?.company_name || 'Cliente', xR + 3, yR);

  yR += 4;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);

  const cityLine = [customer?.postal_code, customer?.city, customer?.province ? `(${customer.province})` : null]
    .filter(Boolean)
    .join(' ');

  const custLines = [
    customer?.address,
    cityLine || null,
    customer?.vat_number ? `P.IVA: ${customer.vat_number}` : null,
    customer?.tax_code ? `C.F.: ${customer.tax_code}` : null,
    customer?.email ? `Email: ${customer.email}` : null,
    customer?.phone ? `Tel: ${customer.phone}` : null,
  ].filter(Boolean) as string[];

  custLines.forEach((line) => {
    doc.text(line, xR + 3, yR);
    yR += 3.6;
  });

  return yStart + boxH + 6;
};

/** Disegna footer aziendale (multi-linea, dati legali completi) + linea separatrice. */
export const drawPageFooter = (
  doc: jsPDF,
  _companyFooter: string = COMPANY_FOOTER_DEFAULT,
  pageWidth: number = A4_PAGE_WIDTH,
  pageHeight: number = A4_PAGE_HEIGHT,
  margin: number = A4_MARGIN
): void => {
  const line1 = `${COMPANY_INFO.ragioneSociale}  •  ${COMPANY_INFO.indirizzo}, ${COMPANY_INFO.cap} ${COMPANY_INFO.citta} (${COMPANY_INFO.provincia})`;
  const line2 = `P.IVA ${COMPANY_INFO.partitaIva}  •  C.F. ${COMPANY_INFO.codiceFiscale}  •  REA ${COMPANY_INFO.rea}  •  ${COMPANY_INFO.regimeFiscaleCode} ${COMPANY_INFO.regimeFiscale}`;
  const line3 = `${COMPANY_INFO.sito}  •  ${COMPANY_INFO.email}  •  PEC ${COMPANY_INFO.pec}  •  Tel ${COMPANY_INFO.telefono}`;

  const baseY = pageHeight - 14;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, baseY - 2, pageWidth - margin, baseY - 2);

  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(110, 110, 110);
  doc.text(line1, pageWidth / 2, baseY + 2, { align: 'center' });
  doc.text(line2, pageWidth / 2, baseY + 5.5, { align: 'center' });
  doc.text(line3, pageWidth / 2, baseY + 9, { align: 'center' });
};
